import type { NominationConfig } from '@/nominations/types'

/**
 * How a nomination page gets its data at runtime.
 *
 * Deliberately *not* a lookup in the registry. These pages are invite-only, so the set of
 * nominees must never reach the browser bundle — anyone could download it and enumerate all of
 * them. Instead each page carries only its own nominee, injected into that page's HTML:
 *
 * - production: `scripts/prerender.mjs` writes the inline script when it prerenders the page
 * - development: `scripts/lib/nomination-api-dev-plugin.mjs` injects the same script
 * - server render: the prerenderer sets the same global before calling `render()`
 *
 * A URL with no injected payload — an unknown slug, or a draft that was never prerendered —
 * simply finds nothing here, and the route renders the site 404.
 */

export const NOMINATION_DATA_GLOBAL = '__UA_NOMINATION__'

type NominationGlobal = typeof globalThis & {
  [NOMINATION_DATA_GLOBAL]?: NominationConfig
}

export const readNominationData = (slug: string | undefined): NominationConfig | undefined => {
  if (!slug) return undefined

  const nomination = (globalThis as NominationGlobal)[NOMINATION_DATA_GLOBAL]

  // Guard against a stale payload after a client-side navigation between two nomination pages.
  return nomination?.slug === slug ? nomination : undefined
}

/**
 * Serialises one nomination into the inline script tag the page carries.
 *
 * The reviewer's surname is stripped on the way out. The page only ever shows his first name,
 * and the serverless endpoint reads the full name from its own copy of the registry rather than
 * from the request, so shipping it here would put it in the page source for no reason.
 */
export const nominationDataScript = (nomination: NominationConfig) => {
  const forBrowser: NominationConfig = {
    ...nomination,
    reviewer: { ...nomination.reviewer, fullName: nomination.reviewer.firstName },
  }
  // `<` is escaped so a value can never terminate the script element early.
  const json = JSON.stringify(forBrowser).replace(/</g, '\\u003c')
  return `<script>window.${NOMINATION_DATA_GLOBAL}=${json}</script>`
}
